import { defineComponent, reactive, watch, computed, ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import _ from 'lodash';
import { SettingTable, SettingTableRow, Selector, ImageDescription } from '@hems/component';
import { CommonService } from '@hems/service';
import PricingSetting from '@hems/user/src/containers/popup/homeinfo/control/PricingSetting.vue';
import { Helper, useACDeviceType } from '@hems/util';
import { AC_DEVICE_TYPE, GEN_TYPE, STORM_MESSAGE } from '@hems/util/src/constant';
import { ENERGY_POLICY } from '@hems/util/src/constant/constant';
export default defineComponent({
    name: 'EnergyPolicySetting',
    components: {
        SettingTable,
        SettingTableRow,
        Selector,
        PricingSetting,
        ImageDescription,
    },
    props: {
        modelValue: {
            type: Object,
            required: true,
        },
        genType: {
            type: String,
            required: true,
        },
        editable: {
            type: Boolean,
            default: false,
        },
        currentEnergyControl: {
            type: String,
            required: true,
        },
    },
    emits: ['update:modelValue'],
    setup(props, { emit }) {
        const { t } = useI18n();
        const store = useStore();
        const commonService = new CommonService(window.axiosInstance.axios);
        const state = reactive({
            data: { ...props.modelValue },
            editable: props.editable,
            deviceId: store.state.user.ownDeviceId,
            energyPolicy: props.modelValue?.energy_policy ?? 0,
        });
        const { isSolarConfig } = useACDeviceType();
        const stormData = computed(() => store.state.storm);
        const isStormCoverSupportedDevice = computed(() => {
            return !!(store.state.device.siteConfiguration?.hasHub && store.state.device.gen_type === GEN_TYPE.AC_SYS);
        });
        const isStormCoverMode = computed(() => props.currentEnergyControl === ENERGY_POLICY.STORM_COVER);
        const isStormCoverModeActive = computed(() => {
            return !!(isStormCoverMode.value && isStormCoverSupportedDevice.value && stormData.value?.stormCoverStatus === 1);
        });
        const isTimeBased = computed(() => state.energyPolicy === parseInt(ENERGY_POLICY.TIME_BASED));
        const acDeviceType = ref(store.state.device.acDeviceType);
        const isConsumptionCT = computed(() => store.state.device.isConsumptionCT);
        const selectorOptionsState = reactive({
            energyPolicy: [],
        });
        const stormOptionState = [{ text: 'Storm Cover', value: ENERGY_POLICY.STORM_COVER }];
        const getMOWOperationModeOptions = (operationModes, defaultSelectorOption) => {
            return Helper.addSelectorOptionAtFirst(Helper.codeNamesToSelectorOptions(operationModes, t).map((item) => {
                if (item.value === ENERGY_POLICY.STAND_BY || item.value === ENERGY_POLICY.ZERO_EXPORT) {
                    return { ...item, disabled: true };
                }
                return item;
            }), defaultSelectorOption);
        };
        const getACSystemOperationModeOptions = (operationModes, defaultSelectorOption) => {
            const operationModeText = {
                [ENERGY_POLICY.SELF_CONSUMPTION]: 'Auto',
                [ENERGY_POLICY.ZERO_EXPORT]: 'Zero Export',
            };
            if (acDeviceType.value === AC_DEVICE_TYPE.AC_COMBINER) {
                operationModes = operationModes.filter((option) => option.code !== ENERGY_POLICY.TIME_BASED);
                if (!isConsumptionCT.value) {
                    operationModes = operationModes.filter((option) => option.code !== ENERGY_POLICY.ZERO_EXPORT);
                }
                return Helper.addSelectorOptionAtFirst(Helper.codeNamesToSelectorOptions(operationModes, t).map((item) => {
                    if (item.value === ENERGY_POLICY.SELF_CONSUMPTION) {
                        item.text = operationModeText[ENERGY_POLICY.SELF_CONSUMPTION];
                    }
                    else if (item.value === ENERGY_POLICY.ZERO_EXPORT) {
                        item.text = operationModeText[ENERGY_POLICY.ZERO_EXPORT];
                    }
                    return item;
                }), defaultSelectorOption);
            }
            return Helper.addSelectorOptionAtFirst(Helper.codeNamesToSelectorOptions(operationModes, t).map((item) => {
                if (item.value === ENERGY_POLICY.ZERO_EXPORT) {
                    item.text = operationModeText[ENERGY_POLICY.ZERO_EXPORT];
                }
                return item;
            }), defaultSelectorOption);
        };
        const loadSelectorOptions = async () => {
            const { ENERGY_POLICY_GEN3_CD } = await commonService.getCodesByGroupCode([
                { grpCd: 'ENERGY_POLICY_GEN3_CD', exclude: [ENERGY_POLICY.EXTERNAL_GENERATION] },
            ]);
            const defaultSelectorOption = { text: t('common.select'), value: null };
            if (props.genType === GEN_TYPE.AC_SYS) {
                selectorOptionsState.energyPolicy = getACSystemOperationModeOptions(ENERGY_POLICY_GEN3_CD, defaultSelectorOption);
            }
            else {
                selectorOptionsState.energyPolicy = getMOWOperationModeOptions(ENERGY_POLICY_GEN3_CD, defaultSelectorOption);
            }
        };
        const updatePricingSettings = (pricingSettingsParams) => {
            emit('update:modelValue', _.pickBy({ ...state.data, ...pricingSettingsParams, energy_policy: state.energyPolicy }, (v) => !Helper.isNull(v)));
        };
        watch(() => state.energyPolicy, () => {
            emit('update:modelValue', _.pickBy({ ...state.data, energy_policy: state.energyPolicy }, (v) => !Helper.isNull(v)));
        });
        onMounted(() => {
            loadSelectorOptions();
        });
        return {
            state,
            selectorOptionsState,
            updatePricingSettings,
            isTimeBased,
            stormData,
            isStormCoverSupportedDevice,
            isStormCoverModeActive,
            stormOptionState,
            STORM_MESSAGE,
            isSolarConfig,
        };
    },
});
